import { faLink } from '@fortawesome/pro-regular-svg-icons';

import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { ArchiveModal } from '@client/new/common/collection/ArchiveModal';
import { DeleteModal } from '@client/new/common/collection/DeleteModal';
import { TitledPage } from '@client/new/components/Misc/TitledPage';
import { TableLoader } from '@client/new/components/TableShared/TableLoader';
import { LinksModal } from '@client/new/pages/collections/components/linksModal';
import { Alert } from '@design-system/components/alert/alert';
import { Button } from '@design-system/components/button/button';

import { ProgramsVM } from './view-model/programs.viewModel.types';
import { useProgramsVM } from './view-model/programs.viewModel';
import { ChannelLandingPageHeaderButton } from './view/components/ChannelLandingPageHeaderButton';
import { CreateMenu } from './view/components/CreateMenu';
import { ProgramCardList } from './view/components/ProgramCardList';
import { ProgramsEmptyState } from './view/components/ProgramsEmptyState';
import { ProgramsFilters } from './view/components/ProgramsFilters';
import { ProgramsListHeader } from './view/components/ProgramsListHeader';
import { ProgramsNoResults } from './view/components/ProgramsNoResults';
import { ArchiveInDraftOfferModal } from './view/components/ArchiveInDraftOfferModal';
import { RemoveFromSubscriptionModal } from './view/components/RemoveFromSubscriptionModal';
import { SellWithSubscriptionModal } from './view/components/SellWithSubscriptionModal';
import * as styles from './Programs.view.css';

type ProgramsViewProps = {
  influencerId?: string;
};

export const ProgramsView = ({ influencerId }: ProgramsViewProps) => {
  const vm = useProgramsVM({ influencerId });

  return (
    <AsyncRenderer
      data={vm}
      loadRender={
        <TitledPage title="Programs">
          <TableLoader />
        </TitledPage>
      }
      renderer={renderProgramsPage}
    />
  );
};

const renderProgramsPage = (vm: ProgramsVM) => {
  const archiveModal =
    vm.state.modal.type === 'archiveInDraftOffer' ? vm.state.modal : null;
  const standardArchiveModal =
    vm.state.modal.type === 'archive' ? vm.state.modal : null;
  const deleteModal = vm.state.modal.type === 'delete' ? vm.state.modal : null;
  const sellWithSubscriptionModal =
    vm.state.modal.type === 'sellWithSubscriptionConfirm'
      ? vm.state.modal
      : null;
  const removeFromSubscriptionModal =
    vm.state.modal.type === 'removeFromSubscriptionConfirm'
      ? vm.state.modal
      : null;

  return (
    <TitledPage title="Programs">
      <div className={styles.content} data-testid="programs-view">
        {vm.state.viewMode.type === 'list' && !vm.state.isDnDEnabled ? (
          <Alert
            variant="info"
            title="Create and manage all your Programs conveniently from a single place."
            details={`To organize Programs in the Consumer app, start by applying the "Sold as..." filter to reorder them effectively.`}
            wrapperClassName={styles.alert}
          />
        ) : null}
        <div className={styles.stickyHeader}>
          <ProgramsFilters
            state={vm.state}
            dispatch={vm.dispatch}
            actions={
              <>
                {vm.state.isCreatorLive ? (
                  <Button
                    variant="text"
                    color="primary"
                    leftIcon={faLink}
                    onClick={() => vm.dispatch('OpenSubLinks')}
                    testId="programs-header-share-links"
                  >
                    Links
                  </Button>
                ) : null}
                <ChannelLandingPageHeaderButton
                  onClick={() => vm.dispatch('OpenChannelLandingPageBuilder')}
                />
                <CreateMenu
                  creatorId={vm.state.creator.id}
                  showUpload={false}
                />
              </>
            }
          />
          {vm.state.viewMode.type === 'list' ? (
            <ProgramsListHeader isDnDEnabled={vm.state.isDnDEnabled} />
          ) : null}
        </div>
        {renderContent(vm)}
      </div>

      <ArchiveModal
        isOpen={standardArchiveModal !== null}
        close={() => vm.dispatch('CloseModal')}
        isConfirming={standardArchiveModal?.inFlight ?? false}
        archive={() => {
          if (!standardArchiveModal) return;
          vm.dispatch('ConfirmArchive', standardArchiveModal.programId);
        }}
      />

      <ArchiveInDraftOfferModal
        isOpen={archiveModal !== null}
        programName={archiveModal?.programName ?? ''}
        offerNames={archiveModal?.offerNames ?? []}
        isConfirming={archiveModal?.inFlight ?? false}
        onConfirm={() => {
          if (!archiveModal) return;
          vm.dispatch('ConfirmArchive', archiveModal.programId);
        }}
        onClose={() => vm.dispatch('CloseModal')}
      />

      <DeleteModal
        isOpen={deleteModal !== null}
        close={() => vm.dispatch('CloseModal')}
        isConfirming={deleteModal?.inFlight ?? false}
        onDelete={() => {
          if (!deleteModal) return;
          vm.dispatch('ConfirmDelete', deleteModal.programId);
        }}
      />

      <LinksModal
        isOpen={vm.state.modal.type === 'links'}
        onClose={() => vm.dispatch('CloseModal')}
        data={toLinksModalData(vm)}
      />

      <SellWithSubscriptionModal
        isOpen={sellWithSubscriptionModal !== null}
        programName={sellWithSubscriptionModal?.programName ?? ''}
        isConfirming={sellWithSubscriptionModal?.inFlight ?? false}
        onConfirm={() => {
          if (!sellWithSubscriptionModal) return;
          vm.dispatch(
            'ConfirmSellWithSubscription',
            sellWithSubscriptionModal.programId,
          );
        }}
        onClose={() => vm.dispatch('CloseModal')}
      />

      <RemoveFromSubscriptionModal
        isOpen={removeFromSubscriptionModal !== null}
        programName={removeFromSubscriptionModal?.programName ?? ''}
        isConfirming={removeFromSubscriptionModal?.inFlight ?? false}
        onConfirm={() => {
          if (!removeFromSubscriptionModal) return;
          vm.dispatch(
            'ConfirmRemoveFromSubscription',
            removeFromSubscriptionModal.programId,
          );
        }}
        onClose={() => vm.dispatch('CloseModal')}
      />
    </TitledPage>
  );
};

const renderContent = (vm: ProgramsVM) => {
  switch (vm.state.viewMode.type) {
    case 'empty':
      return (
        <ProgramsEmptyState
          createMenu={
            <CreateMenu creatorId={vm.state.creator.id} showUpload={false} />
          }
        />
      );
    case 'no-results':
      return <ProgramsNoResults dispatch={vm.dispatch} />;
    case 'list':
      return (
        <ProgramCardList
          rows={vm.state.viewMode.rows}
          isDnDEnabled={vm.state.isDnDEnabled}
          onReorder={(ids) => vm.dispatch('Reorder', ids)}
          dispatch={vm.dispatch}
        />
      );
  }
};

const toLinksModalData = (vm: ProgramsVM) => {
  if (vm.state.modal.type !== 'links') return undefined;

  return {
    programName: vm.state.modal.data.programName,
    links: vm.state.modal.data.links.map((link) => ({
      ...link,
      description: link.description ?? '',
    })),
  };
};
